import { X, Dumbbell, Sparkles, Heart, Users } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

const BrochureModal = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const venueTypes = [
    {
      icon: Dumbbell,
      title: "Salles premium urbaines",
      description: "Clubs de fitness haut de gamme au cœur des grandes métropoles. Clientèle active, urbaine et soucieuse de son image.",
      audience: "CSP+, 25-45 ans, actifs urbains",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400"
    },
    {
      icon: Heart,
      title: "Clubs bien-être",
      description: "Espaces orientés wellness, yoga et récupération. Environnement propice à la découverte sensorielle.",
      audience: "Femmes 30-50 ans, sensibles au bien-être",
      image: "https://images.pexels.com/photos/3822906/pexels-photo-3822906.jpeg?w=400"
    },
    {
      icon: Sparkles,
      title: "Espaces fitness haut de gamme",
      description: "Installations premium avec services personnalisés. Clientèle exigeante recherchant l'excellence.",
      audience: "Clientèle premium, forte affinité luxe",
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400"
    },
    {
      icon: Users,
      title: "Environnements affinitaires",
      description: "Lieux sélectionnés pour leur cohérence avec l'univers des maisons de parfums et du luxe accessible.",
      audience: "Profils sensibles à l'art de vivre",
      image: "https://images.pexels.com/photos/1954524/pexels-photo-1954524.jpeg?w=400"
    }
  ];

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
        style={{ zIndex: 99998 }}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4"
        style={{ zIndex: 99999 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="brochure-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] text-white p-8 z-10">
          <button
            onClick={onClose}
            data-testid="btn-close-brochure"
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={24} />
          </button>
          <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
            Notre réseau
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-light">
            Découvrez nos lieux partenaires
          </h2>
          <p className="font-body text-white/70 mt-4 max-w-xl">
            Un réseau soigneusement sélectionné de salles premium, 
            offrant une présence stratégique auprès d'une clientèle affinitaire.
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Intro */}
          <div className="mb-12">
            <h3 className="font-heading text-2xl text-[#0A0A0A] mb-4">
              Des environnements d'exception
            </h3>
            <p className="font-body text-[#525252] leading-relaxed">
              Le Pschitt Français développe un réseau exclusif de lieux partenaires. 
              Chaque espace est sélectionné pour sa qualité, son positionnement et 
              la pertinence de sa clientèle avec l'univers des maisons de parfums.
            </p>
          </div>

          {/* Venue Types Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {venueTypes.map((venue) => (
              <div key={venue.title} className="border border-[#E5E5E5] overflow-hidden group">
                {/* Image */}
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={venue.image}
                    alt={venue.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute bottom-4 left-4 w-10 h-10 bg-white flex items-center justify-center">
                    <venue.icon size={20} className="text-[#B89F7C]" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h4 className="font-heading text-xl text-[#0A0A0A] mb-2">
                    {venue.title}
                  </h4>
                  <p className="font-body text-sm text-[#525252] mb-4">
                    {venue.description}
                  </p>
                  <div className="bg-[#F9F6F0] p-3">
                    <p className="font-body text-xs uppercase tracking-wider text-[#525252] mb-1">
                      Profil type
                    </p>
                    <p className="font-body text-sm text-[#0A0A0A]">
                      {venue.audience}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Key Numbers */}
          <div className="bg-[#F9F6F0] p-8 mb-12">
            <h3 className="font-heading text-xl text-[#0A0A0A] mb-6">
              Notre réseau en chiffres
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="font-heading text-3xl text-[#B89F7C]">50+</p>
                <p className="font-body text-sm text-[#525252]">Lieux partenaires</p>
              </div>
              <div>
                <p className="font-heading text-3xl text-[#B89F7C]">7</p>
                <p className="font-body text-sm text-[#525252]">Grandes métropoles</p>
              </div>
              <div>
                <p className="font-heading text-3xl text-[#B89F7C]">100K+</p>
                <p className="font-body text-sm text-[#525252]">Adhérents touchés</p>
              </div>
              <div>
                <p className="font-heading text-3xl text-[#B89F7C]">95%</p>
                <p className="font-body text-sm text-[#525252]">Taux de satisfaction</p>
              </div>
            </div>
          </div>

          {/* Promise */}
          <div className="border-l-4 border-[#B89F7C] pl-6 mb-8">
            <p className="font-heading text-xl text-[#0A0A0A] mb-2">
              Notre engagement qualité
            </p>
            <p className="font-body text-[#525252]">
              Chaque lieu de notre réseau est validé selon des critères stricts : 
              qualité des installations, profil de la clientèle, cohérence avec 
              l'univers premium des maisons de parfums.
            </p>
          </div>

          {/* Note */}
          <p className="font-body text-xs text-[#525252] text-center">
            Les lieux spécifiques de déploiement sont déterminés par notre équipe 
            en fonction de votre niveau de diffusion et de vos objectifs.
          </p>
        </div>

        {/* Footer CTA */}
        <div className="sticky bottom-0 bg-white border-t border-[#E5E5E5] p-6 flex justify-end">
          <button
            onClick={onClose}
            className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium"
          >
            Continuer ma configuration
          </button>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render modal at document.body level
  return createPortal(modalContent, document.body);
};

export default BrochureModal;
